import { generatePath } from "react-router-dom";

export const PATHS = {
  root: "/",
  login: "/login",
  maintenance: "/maintenance",
  home: "/home",

  // Messaging
  messaging: "/home/messaging",

  // Entries
  entries: "/home/entries",

  // Talents
  talents: "/home/talents",
  talentProfile: "/home/talents/:talentId/profile",
  talentEntries: "/home/talents/:talentId/entries/:entryId",
  talentEmployment: "/home/talents/:talentId/employment/:employmentId/",
  talentAssignment: "/home/talents/:talentId/assignment/:assignmentId",

  // Customers
  customers: "/home/customers",
  customerBasicInfo: "/home/customers/:customerCompanyId/basicInfo",
  customerWorkLocations: "/home/customers/:customerCompanyId/workLocations",
  customerOrderTemplates: "/home/customers/:customerCompanyId/orderTemplates",
  customerCostCenters: "/home/customers/:customerCompanyId/customerCostCenters",
  customerContracts: "/home/customers/:customerCompanyId/contracts",
  customerUsers: "/home/customers/:customerCompanyId/users",

  // Invoices
  invoices: "/home/invoices",
  invoiceNew: "/home/invoices/new/",
  invoiceEdit: "/home/invoices/edit/:invoiceIds",
  invoicePreview: "/home/invoices/preview/:invoiceIds",
  invoiceExamine: "/home/invoices/examine/:invoiceId",
  invoiceExamineRow: "/home/invoices/examine/:invoiceId/row/:rowId",

  // Orders
  orders: "/home/orders",
  orderNew: "/home/orders/new/",
  orderEdit: "/home/orders/:bundleId/",
  orderCopy: "/home/orders/:bundleId/copy",
  workAgreementEdit: "/home/orders/:bundleId/:workAgreementId/edit",
  workOffer: "/home/orders/:bundleId/offer",

  // JobAds
  jobAds: "/home/jobAds",
  jobAdNew: "/home/jobAds/new/",
  jobAdView: "/home/jobAds/:jobAdId",

  salaries: "/home/salaries",
  feedbacks: "/home/feedbacks",
  settings: "/home/settings/:tab",
} as const;

type CustomerQuery = { org?: string; ecom?: string };
type RequiredCustomerQuery = { org: string; ecom: string };

export const withQuery = (path: string, query?: Record<string, unknown>) => {
  if (!query) return path;
  const usp = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => v != null && usp.set(k, String(v)));
  const qs = usp.toString();
  return qs ? `${path}?${qs}` : path;
};

export const routes = {
  root: () => PATHS.root,
  login: () => PATHS.login,
  maintenance: () => PATHS.maintenance,
  home: () => PATHS.home,

  messaging: () => PATHS.messaging,
  entries: () => PATHS.entries,

  talents: () => PATHS.talents,
  talentProfile: (talentId: string) =>
    generatePath(PATHS.talentProfile, { talentId }),
  talentEntries: (talentId: string, entryId: string) =>
    generatePath(PATHS.talentEntries, { talentId, entryId }),
  talentEmployment: (talentId: string, employmentId: string) =>
    generatePath(PATHS.talentEmployment, { talentId, employmentId }),
  talentAssignment: (talentId: string, assignmentId: string) =>
    generatePath(PATHS.talentAssignment, { talentId, assignmentId }),

  customers: () => PATHS.customers,
  customerBasicInfo: (customerCompanyId: string, query: CustomerQuery) =>
    withQuery(
      generatePath(PATHS.customerBasicInfo, { customerCompanyId }),
      query,
    ),
  customerWorkLocations: (customerCompanyId: string, query: CustomerQuery) =>
    withQuery(
      generatePath(PATHS.customerWorkLocations, { customerCompanyId }),
      query,
    ),
  customerOrderTemplates: (
    customerCompanyId: string,
    query: RequiredCustomerQuery,
  ) =>
    withQuery(
      generatePath(PATHS.customerOrderTemplates, { customerCompanyId }),
      query,
    ),
  customerContracts: (
    customerCompanyId: string,
    query: RequiredCustomerQuery,
  ) =>
    withQuery(
      generatePath(PATHS.customerContracts, { customerCompanyId }),
      query,
    ),
  customerCostCenters: (customerCompanyId: string, query: CustomerQuery) =>
    withQuery(
      generatePath(PATHS.customerCostCenters, { customerCompanyId }),
      query,
    ),
  customerUsers: (customerCompanyId: string, query: CustomerQuery) =>
    withQuery(generatePath(PATHS.customerUsers, { customerCompanyId }), query),

  invoices: () => PATHS.invoices,
  invoiceNew: () => PATHS.invoiceNew,
  invoiceEdit: (invoiceIds: string) =>
    generatePath(PATHS.invoiceEdit, { invoiceIds }),
  invoicePreview: (invoiceIds: string) =>
    generatePath(PATHS.invoicePreview, { invoiceIds }),
  invoiceExamine: (invoiceId: string) =>
    generatePath(PATHS.invoiceExamine, { invoiceId }),
  invoiceExamineRow: (invoiceId: string, rowId: string) =>
    generatePath(PATHS.invoiceExamineRow, { invoiceId, rowId }),

  orders: () => PATHS.orders,
  orderNew: () => PATHS.orderNew,
  orderEdit: (bundleId: string) => generatePath(PATHS.orderEdit, { bundleId }),
  workAgreementEdit: (bundleId: string, workAgreementId: string) =>
    generatePath(PATHS.workAgreementEdit, { bundleId, workAgreementId }),
  workOffer: (bundleId: string) => generatePath(PATHS.workOffer, { bundleId }),
  orderCopy: (bundleId: string) => generatePath(PATHS.orderCopy, { bundleId }),

  salaries: () => PATHS.salaries,
  feedbacks: () => PATHS.feedbacks,
  settings: (tab: string) => generatePath(PATHS.settings, { tab }),

  jobAds: () => PATHS.jobAds,
  jobAdNew: () => PATHS.jobAdNew,
  jobAdView: (jobAdId: string) => generatePath(PATHS.jobAdView, { jobAdId }),
} as const;
